import { FileTextOutlined } from "@ant-design/icons";
import type React from "react";
import { formatNotificationTime, getNotificationMessage } from "./notificationUtils";
import type { AppNotification } from "./types";

interface NotificationsPopoverContentProps {
  notifications: AppNotification[];
  onViewAll?: () => void;
  onNotificationClick: (notification: AppNotification) => void;
}

const NotificationsPopoverContent: React.FC<NotificationsPopoverContentProps> = ({
  notifications,
  onViewAll,
  onNotificationClick,
}) => {
  const isEmpty = notifications.length === 0;

  return (
    <div className="w-[400px] bg-white rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0]">
        <span className="text-base font-semibold text-dark-blue-one">Notifications</span>
        {!isEmpty && (
          <button
            type="button"
            className="text-sm text-primary font-medium bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
            onClick={onViewAll}
          >
            View All
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className="flex items-center justify-center min-h-[220px] px-4">
          <p className="text-[#8C8C8C] text-sm m-0">Nothing to show here</p>
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto">
          {notifications.map((notification) => (
            <button
              key={notification.eventId}
              type="button"
              className="w-full flex items-start gap-3 px-4 py-3 border-b border-[#F0F0F0] last:border-b-0 bg-transparent text-left cursor-pointer hover:bg-[#F7F9FC]"
              onClick={() => onNotificationClick(notification)}
            >
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#f7f9fc] flex items-center justify-center">
                <FileTextOutlined className="!text-primary text-base" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-dark-blue-one truncate">
                  {notification.patientName}
                </div>
                <div className="text-sm text-[#595959] mt-0.5 line-clamp-2">
                  {getNotificationMessage(notification)}
                </div>
              </div>

              <div className="shrink-0 flex items-center gap-2 pt-0.5">
                <span className="text-xs text-[#8C8C8C] whitespace-nowrap">
                  {formatNotificationTime(notification.timestamp)}
                </span>
                {!notification.isRead && (
                  <span
                    className="inline-block w-2 h-2 rounded-full bg-[#FF4D4F]"
                    aria-hidden="true"
                  />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPopoverContent;
